
import { useState, useEffect } from 'react';
import { DeliveryInfo, QuantityPricePair, HelpSettings } from '../types';

/**
 * Formats a Date object into a string like "lunes, 5 de agosto".
 */
export const formatDate = (date: Date): string => {
    const weekdays = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
    const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    
    const dayOfWeek = weekdays[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];
    
    return `${dayOfWeek}, ${day} de ${month}`;
};

/**
 * Returns a YYYY-MM-DD string for a Date (used to check against holidays).
 */
const toDateKey = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

/**
 * Checks if a date is a non-working day (weekend or holiday).
 */
const isNonWorkingDay = (date: Date, holidaySet?: Set<string>): boolean => {
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) return true; // Weekend
    if (holidaySet && holidaySet.has(toDateKey(date))) return true; // Holiday
    return false;
};

/**
 * Adds a specified number of business days (Mon-Fri, excluding holidays) to a given start date.
 */
export const addBusinessDays = (startDate: Date, days: number, holidays?: string[]): Date => {
  const date = new Date(startDate);
  const holidaySet = holidays && holidays.length > 0 ? new Set(holidays) : undefined;
  let addedDays = 0;
  while (addedDays < days) {
    date.setDate(date.getDate() + 1);
    if (!isNonWorkingDay(date, holidaySet)) {
      addedDays++;
    }
  }
  return date;
};

/**
 * Parses a string like "24 horas", "2 días", "2-3 días" into a number of BUSINESS DAYS.
 * @param durationStr The string to parse.
 * @returns The number of days (rounded up for safety).
 */
export const parseDurationToDays = (durationStr: string): number => {
    if (!durationStr) return 0;
    const lower = durationStr.toLowerCase();
    
    // Check for hours
    if (lower.includes('hora') || lower.includes('hr')) {
        const hoursMatch = lower.match(/(\d+)/);
        if (hoursMatch) {
            const hours = parseInt(hoursMatch[1], 10);
            return Math.ceil(hours / 24); 
        }
        return 1; // Default to 1 day if hrs mention but no number
    }

    // Check for days range "2-3" -> take 3
    const rangeMatch = lower.match(/(\d+)-(\d+)/);
    if (rangeMatch) {
        return parseInt(rangeMatch[2], 10);
    }

    // Check for single days number
    const daysMatch = lower.match(/(\d+)/);
    if (daysMatch) {
        return parseInt(daysMatch[1], 10);
    }

    return 0; // Fallback
};

/**
 * Parses a delivery time string (e.g., "Llega en 3-5 días") to get the number of days.
 * Returns the higher number in a range.
 */
export const parseDaysFromDeliveryString = (deliveryString: string): number => {
    // Look for a range first (e.g., "3-5")
    const rangeMatch = deliveryString.match(/(\d+)-(\d+)/);
    if (rangeMatch) {
        return parseInt(rangeMatch[2], 10); // Return the higher end of the range
    }
    // Look for a single number
    const singleMatch = deliveryString.match(/(\d+)/);
    if (singleMatch) {
        return parseInt(singleMatch[1], 10);
    }
    return 1; // Default to 1 day if parsing fails
};

const formatTime = (date: Date): string => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

const cloneWithTime = (reference: Date, hours: number, minutes: number) => {
    const date = new Date(reference);
    date.setHours(hours, minutes, 0, 0);
    return date;
};

const moveToNextAllowedDay = (date: Date, allowedDays: number[]) => {
    while (!allowedDays.includes(date.getDay())) {
        date.setDate(date.getDate() + 1);
    }
};

export interface UrgencyWindow {
    startDate: Date;
    endDate: Date;
    startLabel: string;
    endLabel: string;
    isActive: boolean;
}

export const getUrgencyWindow = (
    priceInfo: QuantityPricePair | null,
    referenceDate: Date = new Date()
): UrgencyWindow | null => {
    if (!priceInfo?.urgency || !priceInfo?.orderCutoffTime || !priceInfo?.deliveryTime) {
        return null;
    }

    const urgencyHours = parseInt(priceInfo.urgency.match(/(\d+)/)?.[1] || '0', 10);
    if (!urgencyHours) {
        return null;
    }

    const [cutoffHours, cutoffMinutes] = priceInfo.orderCutoffTime.split(':').map(Number);
    if (Number.isNaN(cutoffHours) || Number.isNaN(cutoffMinutes)) {
        return null;
    }

    const isHourlyDelivery = /hr|hrs|hora|horas/i.test(priceInfo.deliveryTime);
    const isDailyDelivery = /d[íi]a(s?)/i.test(priceInfo.deliveryTime);

    if (!isHourlyDelivery && !isDailyDelivery) {
        return null;
    }

    const allowedDays = isHourlyDelivery ? [1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5];
    const reference = new Date(referenceDate);

    let startDate = cloneWithTime(reference, cutoffHours, cutoffMinutes);

    if (isHourlyDelivery) {
        const deliveryHours = parseInt(priceInfo.deliveryTime.match(/(\d+)/)?.[1] || '0', 10);
        startDate.setHours(startDate.getHours() - deliveryHours);
    }

    moveToNextAllowedDay(startDate, allowedDays);

    let endDate = new Date(startDate);
    endDate.setHours(endDate.getHours() + urgencyHours);
    moveToNextAllowedDay(endDate, allowedDays);

    if (reference >= endDate) {
        startDate.setDate(startDate.getDate() + 1);
        moveToNextAllowedDay(startDate, allowedDays);

        endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + urgencyHours);
        moveToNextAllowedDay(endDate, allowedDays);
    }

    const isActive = reference >= startDate && reference < endDate;

    return {
        startDate,
        endDate,
        startLabel: formatTime(startDate),
        endLabel: formatTime(endDate),
        isActive,
    };
};


export const calculateDeliveryInfo = (
    priceInfo: QuantityPricePair | null,
    isUrgent: boolean,
    helpSettings: HelpSettings,
    extraProductionDays: number = 0 // New optional parameter
): DeliveryInfo | null => {

    if (!priceInfo || !helpSettings) {
        return null;
    }

    const holidays = helpSettings.holidays || [];
    const holidaySet = holidays.length > 0 ? new Set(holidays) : undefined;

    // Helper: check if a specific date is a holiday
    const isHoliday = (date: Date) => holidaySet ? holidaySet.has(toDateKey(date)) : false;

    // Function to handle the extra days for design/replica
    const applyExtraDays = (date: Date) => {
        if (extraProductionDays > 0) {
            return addBusinessDays(date, extraProductionDays, holidays);
        }
        return date;
    };

    if (isUrgent && priceInfo.urgency && priceInfo.urgencyPrice) {
        const now = new Date();
        const urgencyWindow = getUrgencyWindow(priceInfo, now);

        if (urgencyWindow && urgencyWindow.isActive) {
            const deliveryTimeStr = priceInfo.deliveryTime || '';
            const isDailyDelivery = /d[íi]a(s?)/i.test(deliveryTimeStr);

            let deliveryDate: Date;

            if (isDailyDelivery) {
                deliveryDate = new Date(now);
                deliveryDate.setDate(deliveryDate.getDate() + 1);
                const urgentWorkingDays = [1, 2, 3, 4, 5, 6];
                while (!urgentWorkingDays.includes(deliveryDate.getDay()) || isHoliday(deliveryDate)) {
                    deliveryDate.setDate(deliveryDate.getDate() + 1);
                }
                deliveryDate.setHours(18, 0, 0, 0);
            } else {
                const deliveryHours = parseInt(priceInfo.urgency.match(/\d+/)?.[0] || '1', 10);
                deliveryDate = new Date(now.getTime() + deliveryHours * 60 * 60 * 1000);
                const urgentWorkingDays = [1, 2, 3, 4, 5, 6];
                while (!urgentWorkingDays.includes(deliveryDate.getDay()) || isHoliday(deliveryDate)) {
                    deliveryDate.setDate(deliveryDate.getDate() + 1);
                    deliveryDate.setHours(10, 0, 0, 0);
                }
            }

            // Apply delay even to urgent orders if it's a replica
            deliveryDate = applyExtraDays(deliveryDate);

            return {
                estimatedDeliveryString: formatDate(deliveryDate),
                estimatedDeliveryDate: deliveryDate,
                countdownTarget: urgencyWindow.endDate,
                orderDeadlineString: `Corte exprés ${urgencyWindow.endLabel}`,
                isExpress: true,
                isUrgent: true,
            };
        }
    }

    const deliveryTimeStr = priceInfo.deliveryTime;
    const dbCutoffTimeStr = priceInfo.orderCutoffTime;

    if (!deliveryTimeStr || !dbCutoffTimeStr) {
        return null;
    }
    
    const now = new Date(); 
    const isHourlyDelivery = /hr|hrs|hora|horas/i.test(deliveryTimeStr);
    const workingSchedule = helpSettings.schedule.filter(d => d.enabled);

    let deliveryDate: Date;
    let countdownTarget: Date;
    let finalCutoffTimeStr: string;

    if (isHourlyDelivery) {
        const deliveryHours = parseInt(deliveryTimeStr.match(/\d+/)?.[0] || '1', 10);
        let productionStartDate = new Date(now);

        let foundSlot = false;
        for (let i = 0; i < 14; i++) {
            const checkDate = new Date(now);
            checkDate.setDate(now.getDate() + i);
            checkDate.setHours(0,0,0,0);
            if (isHoliday(checkDate)) continue; // Skip holidays
            const daySchedule = workingSchedule.find(d => d.dayIndex === checkDate.getDay());

            if (daySchedule) {
                const [endH, endM] = daySchedule.end.split(':').map(Number);
                const realCutoffDate = new Date(checkDate);
                realCutoffDate.setHours(endH, endM, 0, 0);
                realCutoffDate.setHours(realCutoffDate.getHours() - deliveryHours);

                if (i === 0 && now < realCutoffDate) {
                    productionStartDate = new Date(now);
                    countdownTarget = realCutoffDate;
                    foundSlot = true;
                    break;
                }
                
                if (i > 0) {
                    const [startH, startM] = daySchedule.start.split(':').map(Number);
                    productionStartDate = new Date(checkDate);
                    productionStartDate.setHours(startH, startM, 0, 0);
                    countdownTarget = realCutoffDate;
                    foundSlot = true;
                    break;
                }
            }
        }
        
        if (!foundSlot) return null;
        
        finalCutoffTimeStr = `${String(countdownTarget!.getHours()).padStart(2, '0')}:${String(countdownTarget!.getMinutes()).padStart(2, '0')}`;
        
        deliveryDate = new Date(productionStartDate);
        let hoursToAdd = deliveryHours;

        while (hoursToAdd > 0) {
            if (isHoliday(deliveryDate)) {
                deliveryDate.setDate(deliveryDate.getDate() + 1);
                deliveryDate.setHours(0, 0, 0, 0);
                continue;
            }
            const daySchedule = workingSchedule.find(d => d.dayIndex === deliveryDate.getDay());
            if (daySchedule) {
                const [startH, startM] = daySchedule.start.split(':').map(Number);
                const scheduleStart = new Date(deliveryDate);
                scheduleStart.setHours(startH, startM, 0, 0);
                
                const [endH, endM] = daySchedule.end.split(':').map(Number);
                const scheduleEnd = new Date(deliveryDate);
                scheduleEnd.setHours(endH, endM, 0, 0);

                if (deliveryDate < scheduleStart) deliveryDate = new Date(scheduleStart);

                if (deliveryDate < scheduleEnd) {
                    const remainingMs = scheduleEnd.getTime() - deliveryDate.getTime();
                    const remainingHrs = remainingMs / (1000 * 60 * 60);

                    if (hoursToAdd <= remainingHrs) {
                        deliveryDate.setTime(deliveryDate.getTime() + hoursToAdd * 3600000);
                        hoursToAdd = 0;
                    } else {
                        hoursToAdd -= remainingHrs;
                        deliveryDate.setDate(deliveryDate.getDate() + 1);
                        deliveryDate.setHours(0, 0, 0, 0);
                    }
                } else {
                    deliveryDate.setDate(deliveryDate.getDate() + 1);
                    deliveryDate.setHours(0, 0, 0, 0);
                }
            } else {
                deliveryDate.setDate(deliveryDate.getDate() + 1);
                deliveryDate.setHours(0, 0, 0, 0);
            }
        }
    } else { // isDailyDelivery
        const dailyWorkingDays = [1, 2, 3, 4, 5];
        const [cutoffHours, cutoffMinutes] = dbCutoffTimeStr.split(':').map(Number);
        finalCutoffTimeStr = dbCutoffTimeStr;

        let orderPlacedDay = new Date(now);
        const cutoffToday = new Date(now);
        cutoffToday.setHours(cutoffHours, cutoffMinutes, 0, 0);

        if (now > cutoffToday || !dailyWorkingDays.includes(now.getDay()) || isHoliday(orderPlacedDay)) {
            orderPlacedDay.setDate(orderPlacedDay.getDate() + 1);
            while (!dailyWorkingDays.includes(orderPlacedDay.getDay()) || isHoliday(orderPlacedDay)) {
                orderPlacedDay.setDate(orderPlacedDay.getDate() + 1);
            }
        }

        countdownTarget = new Date(orderPlacedDay);
        countdownTarget.setHours(cutoffHours, cutoffMinutes, 0, 0);

        const deliveryDays = parseInt(deliveryTimeStr.match(/\d+/)?.[0] || '1', 10);
        deliveryDate = addBusinessDays(orderPlacedDay, deliveryDays, holidays);
        deliveryDate.setHours(18, 0, 0, 0);
    }
    
    // Apply delay for design services
    deliveryDate = applyExtraDays(deliveryDate);

    const hasExpressOption = isHourlyDelivery || (!!priceInfo.urgency && !!priceInfo.urgencyPrice);

    return {
        estimatedDeliveryString: formatDate(deliveryDate),
        estimatedDeliveryDate: deliveryDate,
        countdownTarget: countdownTarget!,
        orderDeadlineString: `Corte de producción hoy ${finalCutoffTimeStr!}`,
        isExpress: hasExpressOption,
        isUrgent: false,
    };
};

/**
 * Genera una clave estable para agrupar entregas.
 * Solo usa componentes que no cambian entre pantallas (fecha en formato YYYY-MM-DD e indicadores de express/urgente).
 */
export const buildDeliveryGroupKey = (deliveryInfo: DeliveryInfo): string => {
    const datePart = deliveryInfo.estimatedDeliveryDate.toISOString().split('T')[0];
    const expressPart = deliveryInfo.isExpress ? 'express' : 'standard';
    const urgentPart = deliveryInfo.isUrgent ? 'urgent' : 'normal';
    return `${datePart}_${expressPart}_${urgentPart}`;
};

/**
 * A custom hook to simulate a decreasing number of available spots as a deadline approaches.
 */
export const useDynamicSpots = (targetDate: Date | null) => {
    const [spots, setSpots] = useState(15);

    useEffect(() => {
        if (!targetDate) {
            setSpots(15);
            return;
        }

        const calculateSpots = () => {
            const now = new Date().getTime();
            const end = targetDate.getTime();
            // The window for spots starts 8 hours before the deadline
            const start = end - 8 * 60 * 60 * 1000;
            
            const totalDuration = end - start;
            const elapsed = now - start;

            if (now < start) {
                return 15; // Before the window starts, spots are full
            }
            if (now >= end) {
                return 0; // After the deadline, spots are gone
            }
            
            const percentageElapsed = elapsed / totalDuration;
            // Spots decrease from 15 down to a minimum of 2
            const spotsGone = Math.floor(percentageElapsed * 13);
            const remainingSpots = 15 - spotsGone;
            
            return Math.max(2, remainingSpots);
        };

        setSpots(calculateSpots());

        const interval = setInterval(() => {
            setSpots(calculateSpots());
        }, 31 * 1000); // Update every 31 seconds to feel more random

        return () => clearInterval(interval);
    }, [targetDate]);

    return spots;
};
